/**
 * Global Application State Management
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import type { Project, TabId } from '../../shared/types/project';
import { getEnabledTabs, getDefaultTab, tabHasContent } from '../../shared/types/project';
import { api } from '../services/api';

// View mode type
type ViewMode = 'homepage' | 'project';

// State interface
interface AppState {
  // Current active tab
  activeTab: TabId;

  // View mode (homepage or project view)
  viewMode: ViewMode;

  // Projects
  projects: Project[];
  currentProject: Project | null;
  projectsLoading: boolean;

  // Project Editor Modal
  projectEditorOpen: boolean;
  editingProject: Project | null; // null = creating new project

  // PDF Panel
  pdfPanelOpen: boolean;
  pdfPanelWidth: number;

  // Dual View Mode (Matterport + BIM synced)
  dualViewMode: boolean;

  // UI State
  projectModalOpen: boolean;
  renameModalOpen: boolean;
  settingsOpen: boolean;

  // Performance Settings
  ramOptimized: boolean; // If true, unmount inactive viewers to save RAM

  // Status
  statusMessage: string;
}

// Action types
type AppAction =
  | { type: 'SET_ACTIVE_TAB'; payload: TabId }
  | { type: 'SET_VIEW_MODE'; payload: ViewMode }
  | { type: 'SET_PROJECTS'; payload: Project[] }
  | { type: 'SET_CURRENT_PROJECT'; payload: Project | null }
  | { type: 'ADD_PROJECT'; payload: Project }
  | { type: 'UPDATE_PROJECT'; payload: Project }
  | { type: 'DELETE_PROJECT'; payload: string }
  | { type: 'SET_PROJECTS_LOADING'; payload: boolean }
  | { type: 'SET_PROJECT_EDITOR'; payload: { open: boolean; project: Project | null } }
  | { type: 'TOGGLE_PDF_PANEL' }
  | { type: 'SET_PDF_PANEL_WIDTH'; payload: number }
  | { type: 'TOGGLE_DUAL_VIEW' }
  | { type: 'SET_PROJECT_MODAL_OPEN'; payload: boolean }
  | { type: 'SET_RENAME_MODAL_OPEN'; payload: boolean }
  | { type: 'SET_STATUS_MESSAGE'; payload: string }
  | { type: 'SET_SETTINGS_OPEN'; payload: boolean }
  | { type: 'SET_RAM_OPTIMIZED'; payload: boolean };

// Initial state
const initialState: AppState = {
  activeTab: 'matterport',
  viewMode: 'homepage', // Start on homepage
  projects: [],
  currentProject: null,
  projectsLoading: true,
  projectEditorOpen: false,
  editingProject: null,
  pdfPanelOpen: false,
  pdfPanelWidth: 400,
  dualViewMode: false,
  projectModalOpen: false,
  renameModalOpen: false,
  settingsOpen: false,
  ramOptimized: false, // Default: keep viewers in memory for fast switching
  statusMessage: 'Ready',
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };

    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };

    case 'SET_PROJECTS':
      return { ...state, projects: action.payload };

    case 'SET_PROJECT_EDITOR':
      return {
        ...state,
        projectEditorOpen: action.payload.open,
        editingProject: action.payload.project,
      };

    case 'SET_CURRENT_PROJECT': {
      const project = action.payload;
      // Land on a tab that has something to show (e.g. skip an empty Virtual Tour tab)
      const enabled = getEnabledTabs(project);
      const activeTab = project && !(enabled.includes(state.activeTab) && tabHasContent(project, state.activeTab))
        ? (getDefaultTab(project) ?? state.activeTab)
        : state.activeTab;
      return { ...state, currentProject: project, activeTab };
    }

    case 'ADD_PROJECT':
      return { ...state, projects: [action.payload, ...state.projects] };

    case 'UPDATE_PROJECT':
      return {
        ...state,
        projects: state.projects.map(p =>
          p.id === action.payload.id ? action.payload : p
        ),
        currentProject:
          state.currentProject?.id === action.payload.id
            ? action.payload
            : state.currentProject,
      };

    case 'DELETE_PROJECT':
      return {
        ...state,
        projects: state.projects.filter(p => p.id !== action.payload),
        currentProject:
          state.currentProject?.id === action.payload ? null : state.currentProject,
      };

    case 'SET_PROJECTS_LOADING':
      return { ...state, projectsLoading: action.payload };

    case 'TOGGLE_PDF_PANEL':
      return { ...state, pdfPanelOpen: !state.pdfPanelOpen };

    case 'SET_PDF_PANEL_WIDTH':
      return { ...state, pdfPanelWidth: action.payload };

    case 'TOGGLE_DUAL_VIEW':
      return { ...state, dualViewMode: !state.dualViewMode };

    case 'SET_PROJECT_MODAL_OPEN':
      return { ...state, projectModalOpen: action.payload };

    case 'SET_RENAME_MODAL_OPEN':
      return { ...state, renameModalOpen: action.payload };

    case 'SET_STATUS_MESSAGE':
      return { ...state, statusMessage: action.payload };

    case 'SET_SETTINGS_OPEN':
      return { ...state, settingsOpen: action.payload };

    case 'SET_RAM_OPTIMIZED':
      return { ...state, ramOptimized: action.payload };

    default:
      return state;
  }
}

// Context types
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Helper functions
  setActiveTab: (tab: TabId) => void;
  setCurrentProject: (project: Project | null) => void;
  togglePdfPanel: () => void;
  toggleDualView: () => void;
  // Homepage navigation
  showHomepage: () => void;
  openProject: (project: Project) => void;
  // Project editor
  openProjectEditor: (project?: Project) => void;
  closeProjectEditor: () => void;
  // Legacy modals (kept for compatibility)
  openProjectModal: () => void;
  closeProjectModal: () => void;
  openRenameModal: () => void;
  closeRenameModal: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  setRamOptimized: (optimized: boolean) => void;
  setStatus: (message: string) => void;
  loadProjects: () => Promise<void>;
  createProject: (name: string) => Promise<Project | null>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
}

// Create context
const AppContext = createContext<AppContextType | null>(null);

// Provider component
export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Helper functions
  const setActiveTab = useCallback((tab: TabId) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
  }, []);

  const setCurrentProject = useCallback((project: Project | null) => {
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
  }, []);

  const togglePdfPanel = useCallback(() => {
    dispatch({ type: 'TOGGLE_PDF_PANEL' });
  }, []);

  const toggleDualView = useCallback(() => {
    dispatch({ type: 'TOGGLE_DUAL_VIEW' });
  }, []);

  // Homepage navigation
  const showHomepage = useCallback(() => {
    dispatch({ type: 'SET_VIEW_MODE', payload: 'homepage' });
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: null });
  }, []);

  const openProject = useCallback((project: Project) => {
    // SET_CURRENT_PROJECT picks a tab with content when the current one does not apply
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
    dispatch({ type: 'SET_VIEW_MODE', payload: 'project' });
  }, [state.activeTab]);

  // Project editor modal
  const openProjectEditor = useCallback((project?: Project) => {
    dispatch({ type: 'SET_PROJECT_EDITOR', payload: { open: true, project: project || null } });
  }, []);

  const closeProjectEditor = useCallback(() => {
    dispatch({ type: 'SET_PROJECT_EDITOR', payload: { open: false, project: null } });
  }, []);

  const openProjectModal = useCallback(() => {
    dispatch({ type: 'SET_PROJECT_MODAL_OPEN', payload: true });
  }, []);

  const closeProjectModal = useCallback(() => {
    dispatch({ type: 'SET_PROJECT_MODAL_OPEN', payload: false });
  }, []);

  const openRenameModal = useCallback(() => {
    dispatch({ type: 'SET_RENAME_MODAL_OPEN', payload: true });
  }, []);

  const closeRenameModal = useCallback(() => {
    dispatch({ type: 'SET_RENAME_MODAL_OPEN', payload: false });
  }, []);

  const openSettings = useCallback(() => {
    dispatch({ type: 'SET_SETTINGS_OPEN', payload: true });
  }, []);

  const closeSettings = useCallback(() => {
    dispatch({ type: 'SET_SETTINGS_OPEN', payload: false });
  }, []);

  const setRamOptimized = useCallback((optimized: boolean) => {
    dispatch({ type: 'SET_RAM_OPTIMIZED', payload: optimized });
  }, []);

  const setStatus = useCallback((message: string) => {
    dispatch({ type: 'SET_STATUS_MESSAGE', payload: message });
  }, []);

  // Project operations via HTTP API
  const loadProjects = useCallback(async () => {
    dispatch({ type: 'SET_PROJECTS_LOADING', payload: true });
    try {
      const projects = await api.project.getAll();
      dispatch({ type: 'SET_PROJECTS', payload: projects });
    } catch (error) {
      console.error('Failed to load projects:', error);
      setStatus('Failed to load projects');
    } finally {
      dispatch({ type: 'SET_PROJECTS_LOADING', payload: false });
    }
  }, [setStatus]);

  const createProject = useCallback(async (name: string): Promise<Project | null> => {
    try {
      const project = await api.project.create({ name });
      dispatch({ type: 'ADD_PROJECT', payload: project });
      dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
      setStatus(`Created project: ${name}`);
      return project;
    } catch (error) {
      console.error('Failed to create project:', error);
      setStatus('Failed to create project');
      return null;
    }
  }, [setStatus]);

  const updateProject = useCallback(async (id: string, updates: Partial<Project>) => {
    try {
      const updated = await api.project.update({ id, updates });
      if (updated) {
        dispatch({ type: 'UPDATE_PROJECT', payload: updated });
        setStatus('Project saved');
      }
    } catch (error) {
      console.error('Failed to update project:', error);
      setStatus('Failed to save project');
    }
  }, [setStatus]);

  const deleteProject = useCallback(async (id: string) => {
    try {
      await api.project.delete(id);
      dispatch({ type: 'DELETE_PROJECT', payload: id });
      setStatus('Project deleted');
    } catch (error) {
      console.error('Failed to delete project:', error);
      setStatus('Failed to delete project');
    }
  }, [setStatus]);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  const value: AppContextType = {
    state,
    dispatch,
    setActiveTab,
    setCurrentProject,
    togglePdfPanel,
    toggleDualView,
    showHomepage,
    openProject,
    openProjectEditor,
    closeProjectEditor,
    openProjectModal,
    closeProjectModal,
    openRenameModal,
    closeRenameModal,
    openSettings,
    closeSettings,
    setRamOptimized,
    setStatus,
    loadProjects,
    createProject,
    updateProject,
    deleteProject,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// Hook to use the context
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
